// vg-binding: P7.D-74
// vg-binding: P7.D-148
// vg-binding: P7.D-149
//
// apps/vendor/src/pages/Orders.tsx
//
// Phase 7 Wave 6 Task 19 — vendor orders list page.
//
// Layout:
//   - OrdersTabs (6 status tabs + All)
//   - OrdersTable with row-level checkboxes
//   - BulkActionBar floats at the bottom when ≥1 row selected (Task 22)
//   - Numbered pagination per BR-PAGING (Total rows · Page X of Y · << < … > >>)

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { FilterBar, PageHeader, Pagination } from '@printway/ui';

import { useTranslation } from '../lib/i18n/index.js';
import { AnnouncementBanner } from '../features/announcements/AnnouncementBanner.js';
import { BulkActionBar } from '../features/orders/BulkActionBar.js';
import { BulkAssizeBar } from '../features/orders/BulkAssizeBar.js';
import { OrderDetailDrawer } from '../features/orders/OrderDetailDrawer.js';
import { OrdersTable } from '../features/orders/OrdersTable.js';
import { OrdersTabs, tabKeyToStatus, type OrdersTabKey } from '../features/orders/OrdersTabs.js';
import {
  useOrdersList,
  type OrderStatus,
  type OrdersListItem,
} from '../features/orders/api.js';

const PAGE_LIMIT = 50;

function rowKey(r: OrdersListItem): string {
  return `${r.order_id}/${r.item_id}`;
}

export function OrdersPage(): JSX.Element {
  const { t } = useTranslation('orders');
  // UAT #2023 — ?filter=ready_to_start từ Dashboard mở thẳng tab Accepted.
  const initialTab: OrdersTabKey =
    new URLSearchParams(window.location.search).get('filter') === 'ready_to_start'
      ? 'accepted'
      : 'all';
  const [tab, setTab] = useState<OrdersTabKey>(initialTab);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [q, setQ] = useState('');
  // Amendment #9 — vendor list filters.
  const [vendorStatusFilter, setVendorStatusFilter] = useState('');
  const [qName, setQName] = useState('');
  const [assignedFrom, setAssignedFrom] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get('focus');
  // UAT #2025/#2023 — Dashboard quick-action links carry ?filter=urgent|
  // ready_to_start which the page previously ignored (tester landed on the
  // unfiltered All list). urgent → SLA-desc sort (most at-risk on top);
  // ready_to_start → Accepted tab (đơn sẵn sàng bắt đầu sản xuất).
  const quickFilter = searchParams.get('filter');
  // R7-2 — drawer state for inline order detail.
  const [drawerRow, setDrawerRow] = useState<OrdersListItem | null>(null);

  // Notification deep-link: when the URL carries ?focus=<assignment_id>,
  // scroll the matching row into view + flash a highlight ring. Strips
  // after the first paint so reloads don't re-scroll.
  useEffect(() => {
    if (!focusId) return;
    const el = document.querySelector<HTMLElement>(
      `[data-testid="orders-row-${focusId}"]`,
    );
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('ring-2', 'ring-pw-primary');
    const t = setTimeout(() => {
      el.classList.remove('ring-2', 'ring-pw-primary');
    }, 2500);
    return () => clearTimeout(t);
  }, [focusId]);

  const status = tabKeyToStatus(tab);
  // Amendment #9 — assign-date inputs (yyyy-mm-dd) → epoch ms range.
  const assignedFromMs = assignedFrom ? Date.parse(`${assignedFrom}T00:00:00`) : NaN;
  const assignedToMs = assignedTo ? Date.parse(`${assignedTo}T23:59:59`) : NaN;
  const query = useOrdersList({
    status: status as OrderStatus | 'all',
    limit: PAGE_LIMIT,
    page,
    q: q || undefined,
    vendor_status: vendorStatusFilter || undefined,
    q_name: qName || undefined,
    // UAT #2025 (round 2) — urgent = deadline GẦN NHẤT trước, tức
    // sla_asc (slaDeadline tăng dần). Lần đầu dùng sla_desc là ngược:
    // BE sort slaDeadline:-1 → đơn còn 5 ngày nổi trên đơn còn 6 giờ.
    ...(quickFilter === 'urgent' ? { sort: 'sla_asc' as const } : {}),
    ...(Number.isFinite(assignedFromMs) ? { assigned_from: assignedFromMs } : {}),
    ...(Number.isFinite(assignedToMs) ? { assigned_to: assignedToMs } : {}),
  });

  const rows = useMemo(() => query.data?.rows ?? [], [query.data?.rows]);
  const total = query.data?.meta.total ?? 0;
  const pageCount = query.data?.meta.page_count ?? 0;

  const handleTabChange = useCallback((next: OrdersTabKey) => {
    setTab(next);
    setPage(1);
    setSelected(new Set());
  }, []);

  const handlePageChange = useCallback((next: number) => {
    setPage(next);
    setSelected(new Set());
  }, []);

  const handleToggle = useCallback((row: OrdersListItem) => {
    const k = rowKey(row);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }, []);

  const handleToggleAll = useCallback(
    (allOn: boolean) => {
      setSelected(allOn ? new Set(rows.map((r) => rowKey(r))) : new Set());
    },
    [rows],
  );

  const selectedRows = useMemo(
    () => rows.filter((r) => selected.has(rowKey(r))),
    [rows, selected],
  );

  return (
    <main
      data-testid="orders-page-root"
      className="flex flex-col gap-4 px-6 py-6 pb-24"
    >
      <PageHeader title={t('page.title')} />

      {/* Admin system announcement targeted at surface=page:orders. */}
      <AnnouncementBanner surface="page:orders" />

      {/* UAT #2025 — banner báo đang xem chế độ Đơn khẩn cấp (SLA gấp nhất
          xếp trước) khi vào từ nút tắt Dashboard. */}
      {quickFilter === 'urgent' ? (
        <div
          data-testid="orders-urgent-banner"
          className="flex items-center justify-between rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-800"
        >
          <span>{t('page.urgentModeBanner')}</span>
          <button
            type="button"
            data-testid="orders-urgent-banner-clear"
            onClick={() => {
              window.location.href = '/orders';
            }}
            className="text-xs font-medium underline"
          >
            {t('page.urgentModeClear')}
          </button>
        </div>
      ) : null}

      <OrdersTabs
        active={tab}
        onChange={handleTabChange}
        counts={query.data?.meta.counts}
      />

      <FilterBar>
        <input
          type="search"
          data-testid="orders-search-input"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
            setSelected(new Set());
          }}
          placeholder={t('page.searchPlaceholder')}
          className="h-9 w-72 rounded-md border border-pw-border bg-pw-bg px-3 text-sm text-pw-text focus:border-pw-primary focus:outline-none focus:ring-1 focus:ring-pw-primary"
        />
        {q ? (
          <button
            type="button"
            data-testid="orders-search-clear"
            onClick={() => {
              setQ('');
              setPage(1);
              setSelected(new Set());
            }}
            className="text-xs text-pw-text-muted hover:text-pw-text"
          >
            {t('page.searchClear')}
          </button>
        ) : null}
      </FilterBar>

      {/* Amendment #9 — list filters: vendor_status, order-name
          search, assign-time range. */}
      <FilterBar>
        <select
          data-testid="orders-filter-vendor-status"
          value={vendorStatusFilter}
          onChange={(e) => {
            setVendorStatusFilter(e.target.value);
            setPage(1);
            setSelected(new Set());
          }}
          className="h-9 rounded-md border border-pw-border bg-pw-bg px-2 text-sm text-pw-text focus:border-pw-primary focus:outline-none focus:ring-1 focus:ring-pw-primary"
        >
          <option value="">{t('filters.allStatuses')}</option>
          <option value="new">{t('list.vendorStatus.new')}</option>
          <option value="assized">{t('list.vendorStatus.assized')}</option>
          <option value="labeled">{t('list.vendorStatus.labeled')}</option>
          <option value="shipped">{t('list.vendorStatus.shipped')}</option>
        </select>
        <input
          type="search"
          data-testid="orders-filter-name"
          value={qName}
          onChange={(e) => {
            setQName(e.target.value);
            setPage(1);
            setSelected(new Set());
          }}
          placeholder={t('filters.namePlaceholder')}
          className="h-9 w-56 rounded-md border border-pw-border bg-pw-bg px-3 text-sm text-pw-text focus:border-pw-primary focus:outline-none focus:ring-1 focus:ring-pw-primary"
        />
        <label className="flex items-center gap-1 text-xs text-pw-text-muted">
          {t('filters.assignedFrom')}
          <input
            type="date"
            data-testid="orders-filter-assigned-from"
            value={assignedFrom}
            onChange={(e) => {
              setAssignedFrom(e.target.value);
              setPage(1);
            }}
            className="h-9 rounded-md border border-pw-border bg-pw-bg px-2 text-sm text-pw-text focus:border-pw-primary focus:outline-none focus:ring-1 focus:ring-pw-primary"
          />
        </label>
        <label className="flex items-center gap-1 text-xs text-pw-text-muted">
          {t('filters.assignedTo')}
          <input
            type="date"
            data-testid="orders-filter-assigned-to"
            value={assignedTo}
            onChange={(e) => {
              setAssignedTo(e.target.value);
              setPage(1);
            }}
            className="h-9 rounded-md border border-pw-border bg-pw-bg px-2 text-sm text-pw-text focus:border-pw-primary focus:outline-none focus:ring-1 focus:ring-pw-primary"
          />
        </label>
        {(vendorStatusFilter || qName || assignedFrom || assignedTo) ? (
          <button
            type="button"
            data-testid="orders-filter-clear"
            onClick={() => {
              setVendorStatusFilter('');
              setQName('');
              setAssignedFrom('');
              setAssignedTo('');
              setPage(1);
              setSelected(new Set());
            }}
            className="text-xs text-pw-text-muted hover:text-pw-text"
          >
            {t('filters.clear')}
          </button>
        ) : null}
      </FilterBar>

      {/* Amendment #7 (254) — Bulk Assize export/import. Always
          visible: TH1 uses selected rows, TH2 uses the current filter. */}
      <BulkAssizeBar
        selectedItemIds={selectedRows.map((r) => r.item_id)}
        status={status}
        q={q || undefined}
      />

      <OrdersTable
        rows={rows}
        isLoading={query.isLoading}
        selected={selected}
        onToggle={handleToggle}
        onToggleAll={handleToggleAll}
        onOpenDetail={setDrawerRow}
      />

      <Pagination
        currentPage={page}
        pageCount={pageCount}
        totalRows={total}
        testId="orders-pagination"
        disabled={query.isFetching}
        onChange={handlePageChange}
      />

      {/* R6-5 (2026-05-16) — bulk-action UX removed because mixed-status
          selection caused per-row transition errors (mismatched FSM).
          Per-row "Mở chi tiết" CTA in the table now points to the canonical
          /orders/:id/items/:itemId page where ActionBar exposes the right
          actions per status. */}
      {/* eslint-disable-next-line no-constant-condition -- bulk-action bar intentionally disabled (R6-5); `&& false` keeps the JSX for quick re-enable */}
      {selectedRows.length > 0 && false ? (
        <BulkActionBar
          selected={selectedRows}
          onClear={() => setSelected(new Set())}
          onMutated={() => {
            setSelected(new Set());
            void query.refetch();
          }}
        />
      ) : null}

      <OrderDetailDrawer
        open={drawerRow !== null}
        orderId={drawerRow?.order_id ?? null}
        itemId={drawerRow?.item_id ?? null}
        onClose={() => {
          setDrawerRow(null);
          void query.refetch();
        }}
      />
    </main>
  );
}

export default OrdersPage;
